import React from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';

const TermsConditionsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Backdrop */}
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
          onClick={onClose}
        />
        
        {/* Modal */}
        <div className="relative w-full max-w-4xl bg-white rounded-lg shadow-xl max-h-[90vh] overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
            <h2 className="text-2xl font-bold text-gray-900">Terms & Conditions</h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <div className="prose prose-blue max-w-none">
              <div className="mb-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">Terms & Conditions for East End Prep Sports</h1>
                <p className="text-gray-600">Last Updated: August 23, 2025</p>
              </div>

              <div className="space-y-8">
                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h2>
                  <p className="text-gray-700 leading-relaxed">
                    By accessing or using <code className="bg-gray-100 px-2 py-1 rounded text-sm">eastendprepsports.com</code> ("Site"), you ("you" or "visitor") agree to abide by these Terms & Conditions ("Terms"), as well as our Privacy Policy. If you do not agree with any part of these Terms, please do not use the Site.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Purpose of the Site</h2>
                  <p className="text-gray-700 leading-relaxed">
                    The Site provides information, news, schedules, photos, and updates related to the elementary school's sports programs.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">3. Use of the Site</h2>
                  <p className="text-gray-700 mb-3">By using this Site, you agree to:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Access content for personal, non-commercial use only.</li>
                    <li>Refrain from posting or transmitting material that is unlawful, harmful, defamatory, offensive, or otherwise inappropriate.</li>
                    <li>Respect intellectual property rights—content is either owned by the Site or used with permission.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Account Registration (if applicable)</h2>
                  <p className="text-gray-700 mb-3">If registration is required for participation or to submit content:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>Provide accurate and up-to-date information.</li>
                    <li>Safeguard your account credentials; you're responsible for all activity under your account.</li>
                    <li>Notify us immediately of any unauthorized use.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Submitting Photos, Videos, or Content</h2>
                  <p className="text-gray-700 mb-3">If you submit media or other content:</p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>You must have consent from parents or guardians if minors are involved.</li>
                    <li>You grant the Site an irrevocable, royalty-free, worldwide license to reproduce, edit, publish, and distribute such content for promotional or informational purposes.</li>
                    <li>You affirm that your submission doesn't violate any rights (privacy, intellectual property, etc.).</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Intellectual Property</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>All rights to the Site's content—such as text, graphics, logos, and media—are owned by us or our licensors.</li>
                    <li>You agree not to reproduce, distribute, or create derivative works without prior written permission.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Third-Party Links & Disclaimer</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>The Site may contain external links (e.g., school district, social media). These are provided for convenience; we make no warranties regarding their content.</li>
                    <li><strong>Disclaimer of warranties:</strong> The Site and its content are available "as is." We make no guarantees as to accuracy, availability, or fitness for a specific purpose.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Limitation of Liability</h2>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700">
                    <li>To the fullest extent permitted by law, the Site and its affiliates are not liable for any indirect, incidental, or consequential damages.</li>
                    <li>Your only remedy for dissatisfaction is to discontinue use of the Site.</li>
                  </ul>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Indemnification</h2>
                  <p className="text-gray-700">
                    You agree to indemnify and hold harmless East End Prep Sports, its staff, affiliates, and partners from any claims, damages, or expenses arising from your misuse of the Site or violation of these Terms.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Governing Law</h2>
                  <p className="text-gray-700">
                    These Terms are governed by U.S. and Tennessee state law. Any disputes must be resolved through local courts in Tennessee.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Modification of Terms</h2>
                  <p className="text-gray-700">
                    We reserve the right to update these Terms at any time. Updated versions will be posted on the Site, with the "Last Updated" date revised accordingly. Changes become effective upon posting. Your continued use constitutes acceptance of those changes.
                  </p>
                </section>

                <section>
                  <h2 className="text-xl font-semibold text-gray-900 mb-3">12. Contact Us</h2>
                  <p className="text-gray-700">
                    For questions or concerns about these Terms, please contact us.
                  </p>
                </section>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 bg-gray-50 px-6 py-4">
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsConditionsModal;